import React from "react";
import * as RadixContextMenu from "@radix-ui/react-context-menu";
import { useEditor, useValue } from "@tldraw/tldraw";
import { copyAs, exportAs } from "tldraw";
import {
  safeSendToBack,
  safeSendBackward,
  safeBringForward,
} from "pages/flyer/builder/components/SideBar/Aside/components/Shapes/UiOverrides";

const menuItemClass =
  "flex items-center px-2 py-[8px] mt-[2px] text-[12px] text-black font-normal cursor-pointer rounded-[4px] outline-none hover:bg-[#f1f0ff] data-[highlighted]:bg-[#f1f0ff] first:mt-0";
const separatorClass = "h-px bg-[#e8e8e8] my-[4px] mx-1";
const contentClass =
  "w-[128px] bg-white rounded-[6px] p-[4px] shadow-[0px_2px_12px_rgba(0,0,0,0.12),0px_0px_0px_1px_rgba(0,0,0,0.08)] z-[99999]";
const subContentClass =
  "w-[154px] bg-white rounded-[6px] p-[4px] shadow-[0px_2px_12px_rgba(0,0,0,0.12),0px_0px_0px_1px_rgba(0,0,0,0.08)] z-[99999]";
const kbdClass = "ml-auto pl-3 text-[11px] text-[hsl(0,0%,18%)] whitespace-nowrap";

const isMac = typeof navigator !== "undefined" && /Mac/i.test(navigator.platform);
const mod = isMac ? "⌘ " : "Ctrl ";
const shift = isMac ? "⇧ " : "Shift ";

const ChevronRight = () => (
  <svg className="ml-auto" width="7" height="10" viewBox="0 0 7 10" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M1.5 1.5L5 5L1.5 8.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** Ensure editor is in select tool and the target shapes are selected */
function ensureReady(editor: any, targetShapeId?: string, targetShapeIds?: string[]) {
  if (!editor.isIn("select")) {
    editor.setCurrentTool("select");
  }
  // Restore the full selection (multi-select) in case tldraw changed it
  if (targetShapeIds && targetShapeIds.length > 0) {
    const currentIds = editor.getSelectedShapeIds();
    const isSame =
      targetShapeIds.length === currentIds.length && targetShapeIds.every((id: string) => currentIds.includes(id));
    if (!isSame) {
      const shapes = targetShapeIds.map((id: string) => editor.getShape(id)).filter(Boolean);
      if (shapes.length > 0) {
        editor.setSelectedShapes(shapes);
      }
    }
  } else if (targetShapeId && !editor.getSelectedShapeIds().includes(targetShapeId)) {
    editor.select(editor.getShape(targetShapeId));
  }
}

/** Helper: deselect, wait a frame, run action, re-select */
async function withDeselect(editor: any, ids: any[], fn: () => Promise<void>) {
  editor.selectNone();
  await new Promise((r) => requestAnimationFrame(r));
  try {
    await fn();
  } finally {
    editor.select(...ids);
  }
}

/** Reorder submenu */
function ReorderSubmenu({ targetShapeId, targetShapeIds }: { targetShapeId?: string; targetShapeIds?: string[] }) {
  const editor = useEditor();
  return (
    <RadixContextMenu.Root dir="ltr">
      <RadixContextMenu.TriggerItem className={menuItemClass}>
        Reorder
        <ChevronRight />
      </RadixContextMenu.TriggerItem>
      <RadixContextMenu.Content className={subContentClass} sideOffset={4}>
        <RadixContextMenu.Item
          className={menuItemClass}
          onSelect={() => {
            ensureReady(editor, targetShapeId, targetShapeIds);
            editor.bringToFront(editor.getSelectedShapeIds());
          }}
        >
          Bring to front
          <span className={kbdClass}>
            {shift}
            {mod}]
          </span>
        </RadixContextMenu.Item>
        <RadixContextMenu.Item
          className={menuItemClass}
          onSelect={() => {
            ensureReady(editor, targetShapeId, targetShapeIds);
            safeBringForward(editor);
          }}
        >
          Bring forward
          <span className={kbdClass}>{mod}]</span>
        </RadixContextMenu.Item>
        <RadixContextMenu.Item
          className={menuItemClass}
          onSelect={() => {
            ensureReady(editor, targetShapeId, targetShapeIds);
            safeSendBackward(editor);
          }}
        >
          Send backward
          <span className={kbdClass}>{mod}[</span>
        </RadixContextMenu.Item>
        <RadixContextMenu.Item
          className={menuItemClass}
          onSelect={() => {
            ensureReady(editor, targetShapeId, targetShapeIds);
            safeSendToBack(editor);
          }}
        >
          Send to back
          <span className={kbdClass}>
            {shift}
            {mod}[
          </span>
        </RadixContextMenu.Item>
      </RadixContextMenu.Content>
    </RadixContextMenu.Root>
  );
}

/** Copy as submenu (PNG / SVG) */
function CopyAsSubmenu() {
  const editor = useEditor();
  return (
    <RadixContextMenu.Root dir="ltr">
      <RadixContextMenu.TriggerItem className={menuItemClass}>
        Copy as
        <ChevronRight />
      </RadixContextMenu.TriggerItem>
      <RadixContextMenu.Content className={subContentClass} sideOffset={4}>
        <RadixContextMenu.Item
          className={menuItemClass}
          onSelect={() => {
            const ids = [...editor.getSelectedShapeIds()];
            if (ids.length === 0) return;
            withDeselect(editor, ids, () => copyAs(editor, ids, "png", { background: false, padding: 0 }));
          }}
        >
          Copy as PNG
        </RadixContextMenu.Item>
        <RadixContextMenu.Item
          className={menuItemClass}
          onSelect={() => {
            const ids = [...editor.getSelectedShapeIds()];
            if (ids.length === 0) return;
            withDeselect(editor, ids, () => copyAs(editor, ids, "svg", { background: false, padding: 0 }));
          }}
        >
          Copy as SVG
        </RadixContextMenu.Item>
      </RadixContextMenu.Content>
    </RadixContextMenu.Root>
  );
}

/** Export as submenu (PNG / SVG) */
function ExportAsSubmenu() {
  const editor = useEditor();
  return (
    <RadixContextMenu.Root dir="ltr">
      <RadixContextMenu.TriggerItem className={menuItemClass}>
        Export as
        <ChevronRight />
      </RadixContextMenu.TriggerItem>
      <RadixContextMenu.Content className={subContentClass} sideOffset={4}>
        <RadixContextMenu.Item
          className={menuItemClass}
          onSelect={() => {
            const ids = [...editor.getSelectedShapeIds()];
            if (ids.length === 0) return;
            withDeselect(editor, ids, () => exportAs(editor, ids, "png", undefined, { background: false, padding: 0 }));
          }}
        >
          Export as PNG
        </RadixContextMenu.Item>
        <RadixContextMenu.Item
          className={menuItemClass}
          onSelect={() => {
            const ids = [...editor.getSelectedShapeIds()];
            if (ids.length === 0) return;
            withDeselect(editor, ids, () => exportAs(editor, ids, "svg", undefined, { background: false, padding: 0 }));
          }}
        >
          Export as SVG
        </RadixContextMenu.Item>
      </RadixContextMenu.Content>
    </RadixContextMenu.Root>
  );
}

/** Edit submenu with Flatten and Toggle lock */
function EditSubmenu({ targetShapeId, targetShapeIds }: { targetShapeId?: string; targetShapeIds?: string[] }) {
  const editor = useEditor();
  return (
    <RadixContextMenu.Root dir="ltr">
      <RadixContextMenu.TriggerItem className={menuItemClass}>
        Edit
        <ChevronRight />
      </RadixContextMenu.TriggerItem>
      <RadixContextMenu.Content className={subContentClass} sideOffset={4}>
        <RadixContextMenu.Item
          className={menuItemClass}
          onSelect={() => {
            ensureReady(editor, targetShapeId, targetShapeIds);
            // flattenShapesToImages is internal; use the action for this one
            const ids = editor.getSelectedShapeIds();
            if (ids.length === 0) return;
            editor.markHistoryStoppingPoint("flatten");
            // Flatten is complex (async image export) — trigger via keyboard shortcut simulation
            document.dispatchEvent(new KeyboardEvent("keydown", { key: "Enter", shiftKey: true, bubbles: true }));
          }}
        >
          Flatten
        </RadixContextMenu.Item>
        <RadixContextMenu.Item
          className={menuItemClass}
          onSelect={() => {
            ensureReady(editor, targetShapeId, targetShapeIds);
            const ids = editor.getSelectedShapeIds();
            if (ids.length === 0) return;
            editor.markHistoryStoppingPoint("toggle-lock");
            editor.toggleLock(ids);
          }}
        >
          Toggle locked
          <span className={kbdClass}>{mod}L</span>
        </RadixContextMenu.Item>
      </RadixContextMenu.Content>
    </RadixContextMenu.Root>
  );
}

export function LayersContextMenuContent({
  targetShapeId,
  targetShapeIds,
}: {
  targetShapeId?: string;
  targetShapeIds?: string[];
}) {
  const editor = useEditor();

  const hasSelection = useValue("hasSelection", () => editor.getSelectedShapeIds().length > 0, [editor]);
  const hasTwoOrMore = useValue("hasTwoOrMore", () => editor.getSelectedShapeIds().length >= 2, [editor]);
  const hasGroupSelected = useValue(
    "hasGroupSelected",
    () => editor.getSelectedShapeIds().some((id) => editor.getShape(id)?.type === "group"),
    [editor]
  );

  if (!hasSelection) return null;

  return (
    <RadixContextMenu.Content className={contentClass} sideOffset={5} alignOffset={-5}>
      {/* Edit */}
      <EditSubmenu targetShapeId={targetShapeId} targetShapeIds={targetShapeIds} />
      {/* Reorder */}
      <ReorderSubmenu targetShapeId={targetShapeId} targetShapeIds={targetShapeIds} />

      <RadixContextMenu.Separator className={separatorClass} />

      {/* Group / Ungroup */}
      {hasTwoOrMore && (
        <RadixContextMenu.Item
          className={menuItemClass}
          onSelect={() => {
            ensureReady(editor, targetShapeId, targetShapeIds);
            editor.markHistoryStoppingPoint("group");
            editor.groupShapes(editor.getSelectedShapeIds());
          }}
        >
          Group
          <span className={kbdClass}>{mod}G</span>
        </RadixContextMenu.Item>
      )}
      {hasGroupSelected && (
        <RadixContextMenu.Item
          className={menuItemClass}
          onSelect={() => {
            ensureReady(editor, targetShapeId, targetShapeIds);
            editor.markHistoryStoppingPoint("ungroup");
            editor.ungroupShapes(editor.getSelectedShapeIds());
          }}
        >
          Ungroup
          <span className={kbdClass}>
            {shift}
            {mod}G
          </span>
        </RadixContextMenu.Item>
      )}
      {(hasTwoOrMore || hasGroupSelected) && <RadixContextMenu.Separator className={separatorClass} />}

      {/* Clipboard + Delete */}
      <RadixContextMenu.Item
        className={menuItemClass}
        onSelect={() => {
          ensureReady(editor, targetShapeId, targetShapeIds);
          editor.markHistoryStoppingPoint("duplicate");
          editor.duplicateShapes(editor.getSelectedShapeIds());
        }}
      >
        Duplicate
        <span className={kbdClass}>{mod}D</span>
      </RadixContextMenu.Item>
      <RadixContextMenu.Item
        className={menuItemClass}
        onSelect={() => {
          // Copy uses clipboard API — simulate keyboard shortcut
          document.execCommand("copy");
        }}
      >
        Copy
        <span className={kbdClass}>{mod}C</span>
      </RadixContextMenu.Item>
      <RadixContextMenu.Item
        className={menuItemClass}
        onSelect={() => {
          document.execCommand("cut");
        }}
      >
        Cut
        <span className={kbdClass}>{mod}X</span>
      </RadixContextMenu.Item>
      <RadixContextMenu.Item
        className={menuItemClass}
        onSelect={() => {
          document.execCommand("paste");
        }}
      >
        Paste
        <span className={kbdClass}>{mod}V</span>
      </RadixContextMenu.Item>
      <RadixContextMenu.Item
        className={menuItemClass}
        onSelect={() => {
          ensureReady(editor, targetShapeId, targetShapeIds);
          editor.markHistoryStoppingPoint("delete");
          editor.deleteShapes(editor.getSelectedShapeIds());
        }}
      >
        Delete
        <span className={kbdClass}>⌫</span>
      </RadixContextMenu.Item>

      <RadixContextMenu.Separator className={separatorClass} />

      {/* Copy as / Export as */}
      <CopyAsSubmenu />
      <ExportAsSubmenu />

      <RadixContextMenu.Separator className={separatorClass} />

      {/* Select all */}
      <RadixContextMenu.Item
        className={menuItemClass}
        onSelect={() => {
          ensureReady(editor, targetShapeId, targetShapeIds);
          editor.selectAll();
        }}
      >
        Select all
        <span className={kbdClass}>{mod}A</span>
      </RadixContextMenu.Item>
    </RadixContextMenu.Content>
  );
}
